import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import Scene from '../components/albumart/Scene';
import Player from '../components/albumart/Player';

export default function Album() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const audioSrc = useMemo(() => {
    return '/mp3s/bftfl-mmr.mp3';
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    const onLoaded = () => setDuration(audio.duration || 0);
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onError = () => {
      const err = audio.error;
      if (err) {
        // Basic error logging to help diagnose browser-specific issues (e.g., MIME/CORS)
        console.error('Audio error code:', err.code);
      } else {
        console.error('Audio error: unknown');
      }
    };

    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('loadedmetadata', onLoaded);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('error', onError);
    // Ensure browser loads the nested <source> with explicit type
    audio.load();
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('loadedmetadata', onLoaded);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('error', onError);
    };
  }, []);

  const progressPct = useMemo(() => {
    if (!duration) return 0;
    return Math.min(100, (currentTime / duration) * 100);
  }, [currentTime, duration]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-gray-200">
      <div className="absolute inset-0 z-0">
        <Scene isPlaying={isPlaying} audioRef={audioRef} />
      </div>
      {/* Bottom gradient overlay to create a floor-like darkness */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-1/2 bg-gradient-to-t from-black/50 to-transparent" />

      <Player
        isPlaying={isPlaying}
        onTogglePlay={togglePlay}
        progressPct={progressPct}
        currentTime={currentTime}
        duration={duration}
      />
      <audio hidden ref={audioRef} preload="auto" controls playsInline>
        <source src={audioSrc} type="audio/mpeg" />
        {/* Fallback text */}
        Your browser does not support the audio element.
      </audio>
    </div>
  );
}
